import { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import config, {
  EnvConfig,
} from "../../../config/index";
import labels from "../../../config/labels";
import {
  CustomContainer,
  useRouter,
  connect,
} from "../../atoms/DependencyHelper";
import { Cookie } from "../../atoms/DependencyHelper";
import CTAButton from "../../atoms/CTAButton/CTAButton";
import Image from "../../atoms/image/image";
import { getSalesOrder } from "../../../services/checkoutService";
import {
  showSpinner,
  hideSpinner,
  getCookieValue,
  urlPrefix,
  imagePrefix,
  setCookieConsentWrapper,
} from "../../../utils/helperServices";
import { productOrderReq } from "../../../data/mockRenderComponent";
import * as Analytics from "../../../services/analyticsUtag";
import {
  setNormalCheckoutCurrentStep,
  setPostCheckoutCurrentStep,
  removeCreditCheckStates,
  setPaymentMethodUrl,
  setPostOrderResponse,
} from "../../../Redux/actions/checkoutAction";
import paymentServices from "../../../utils/paymentIntegrationServices";
import styles from "./CheckoutPaymentError.module.scss";

const CheckoutPaymentError = forwardRef(
  (
    {
      navigate,
      profileData,
      userLoggedIn,
      customerStatus,
      applicationType,
      setNormalCheckoutCurrentStep,
      setPostCheckoutCurrentStep,
      removeCreditCheckStates,
      setPaymentMethodUrl,
      setPostOrderResponse,
      paymentOptionsEnabled,
      paymentConfiguration,
      isMiniApp,
    },
    ref
  ) => {
    const router = useRouter();
    const authToken = getCookieValue("auth_token");
    const vodAuthToken = getCookieValue("vod-web-auth-token");
    const analyticsData = Analytics.getUtagData();

    const [postOrderData, setPostOrderData] = useState({});
    const [paymentUrl, setPaymentUrl] = useState(null);

    const env = config.env;
    const paymentCallbackUrl =
      applicationType == "normal"
        ? config.envConfigs[env].paymentCallback
        : config.envConfigs[env].postPaymentCallback;
    const paymentProvision = config.envConfigs[env].paymentProvision;
    const paymentConfig = paymentConfiguration;
    const customerIdentifier =
      userLoggedIn && profileData?.profileID ? profileData?.profileID : "";

    const orderId = getCookieValue(config.order_id);
    const failCount = getCookieValue(config.pay_fail_count);
    const [retryCount, setRetryCount] = useState(
      failCount && parseInt(failCount) > 2
        ? 2
        : failCount
        ? parseInt(failCount)
        : 0
    );
    const technicalError = orderId ? false : true;

    useImperativeHandle(ref, () => ({
      back: () => {
        if (applicationType == "normal") {
          Analytics.link({
            link_type: "o",
            link_name: `checkout: make payment: ${customerStatus}: back to delivery`,
          });
        } else {
          Analytics.link({
            link_type: "o",
            link_name: `checkout: make payment: ${customerStatus}: back to lets get started`,
          });
        }
      },
    }));

    const retryBtnConfig = {
      buttonStyle: "solid red",
      buttonLabel: "Try again",
    };
    const exitBtnConfig = {
      buttonStyle: "solid red",
      buttonLabel: "Exit checkout",
    };

    let headerText = "";
    let descriptionText = "";
    let noteText = "";

    if (!technicalError) {
      if (retryCount < 2) {
        headerText =
          labels.lbl_payment_failure_heading +
          (retryCount == 1 ? " again" : "");
        descriptionText = labels.lbl_payment_failure_description;
        noteText = labels.lbl_payment_failure_note;
      } else {
        headerText = labels.lbl_payment_failure_heading_error;
        descriptionText = userLoggedIn
          ? labels.lbl_payment_failure_description_error
          : labels.lbl_payment_failure_description_error_guest;
        noteText = "";
      }
    } else {
      headerText = labels.lbl_payment_failure_heading_tech_error;
      descriptionText = labels.lbl_payment_failure_description_tech_error;
      noteText = "";
    }

    useEffect(() => {
      if (customerStatus) {
        Analytics.view({
          page_event: "view",
          page_name: `checkout: make payment: ${customerStatus}`,
          ...Analytics.getProfileData(userLoggedIn, profileData),
        });

        Analytics.error(`${headerText}. ${descriptionText}`);
      }
    }, [customerStatus]);

    const handleRetry = (e) => {
      e.preventDefault();
      Analytics.link({
        link_type: "o",
        link_name: `checkout: make payment: ${customerStatus}: try again`,
        page_name: `checkout: make payment: ${customerStatus}`,
        visitor_login_status: analyticsData?.visitor_login_status,
      });

      if (orderId) {
        placeOrder();
      } else {
        clearReduxState(false);

        if (applicationType == "normal") {
          Analytics.link({
            link_type: "o",
            link_name: `checkout: make payment: ${customerStatus}: back to delivery`,
          });
          navigate("delivery");
        } else {
          Analytics.link({
            link_type: "o",
            link_name: `checkout: make payment: ${customerStatus}: back to lets get started`,
          });
          navigate("authentication");
        }
      }
    };

    const clearReduxState = async (removeGuest = true) => {
      setNormalCheckoutCurrentStep(null);
      setPostCheckoutCurrentStep(null);
      removeCreditCheckStates(null);
      setPaymentMethodUrl(null);
      setPostOrderResponse({});

      Cookie.remove(config.order_id);
      Cookie.remove(config.pay_fail_count);
      if (removeGuest) {
        Cookie.remove("guest_checkout");
      }
    };

    //Clearing cookies on exit
    const handleExitCheckout = async () => {
      showSpinner();

      if (userLoggedIn) {
        Analytics.link({
          link_type: "o",
          link_name: `checkout: make payment: ${customerStatus}: view order history`,
          page_name: `checkout: make payment: ${customerStatus}`,
        });
        await router.push(`${urlPrefix}/view-order-history`);
      } else {
        Analytics.link({
          link_type: "o",
          link_name: `checkout: make payment: ${customerStatus}: exit checkout`,
          page_name: `checkout: make payment: ${customerStatus}`,
        });
        await router.push(`${urlPrefix}/products`);
      }
      await clearReduxState();
    };

    const placeOrder = async () => {
      try {
        showSpinner();
        let results = await getSalesOrder(orderId);
        if (!results.fail) {
          if (results.order && Object.keys(results.order).length > 0) {
            orderRouter(results);
          } else {
            updateRetryCount();
            hideSpinner();
          }
        } else {
          updateRetryCount();
          hideSpinner();
        }

        if (results && results.errors) {
          Analytics.link({
            ...Analytics.getUtagData(["page_name"]),
            page_form_event: "error",
            page_errors: `${
              results.errors[0] ? results.errors?.[0].message : ""
            }`,
            page_errors_code: `chk-dxl-g-so-${results.status}`,
          });
        }
      } catch {
        updateRetryCount();
        hideSpinner();
      }
    };

    const orderRouter = (orderResult) => {
      let headers = {};
      let postOrder = JSON.parse(
        JSON.stringify(productOrderReq).replace(/\:null/gi, ':""')
      ); //replace null with "" for all attributes
      postOrder.productOrder[0].productOrderState =
        orderResult?.order?.order_status;
      postOrder.productOrder[0].productOrderId = orderId;
      postOrder.productOrder[0].orderReference = orderResult?.order
        ?.waybill_number
        ? orderResult.order.waybill_number
        : "";

      if (vodAuthToken) {
        headers = {
          "Content-Type": "application/json",
          Accept: "application/json",
          "x-auth-jwt": `Bearer ${vodAuthToken}`,
          "x-magento-token": `Bearer ${authToken}`,
        };
      } else {
        headers = {
          "Content-Type": "application/json",
          Accept: "application/json",
        };
      }

      const url = `${config.envConfigs[config.env].dxl.url}${
        config["api"]["postpaidProductOrder"]
      }`;

      axios
        .post(url, postOrder, {
          headers: headers,
        })
        .then((response) => {
          const { data } = response || {};

          if (data && data?.productOrders?.length) {
            setPostOrderData(data.productOrders[0]);

            if (data.productOrders[0]?.payment) {
              if (paymentOptionsEnabled) {
                getIntentData(data.productOrders[0].productOrderId);
              } else {
                redirectToPayment();
              }
            } else {
              Cookie.remove(config.pay_fail_count);
              hideSpinner();
              navigate("orderConfirmation");
            }
          } else {
            updateRetryCount();
            hideSpinner();
          }
        })
        .catch((err) => {
          if (err) {
            Analytics.link({
              ...Analytics.getUtagData(["page_name"]),
              page_form_event: "error",
              page_errors: `${err.message ? err.message : ""}`,
              page_errors_code: `chk-dxl-p-po-${err?.response?.status}`,
            });
          }
          updateRetryCount();
          hideSpinner();
        });
    };

    const getIntentData = async (productOrderId) => {
      paymentServices
        .getPaymentIntent(productOrderId, paymentCallbackUrl)
        .then((res) => {
          if (!res.fail && res.response?.data) {
            setPaymentUrl(res.response.data?.paymentUrl);
          } else {
            Analytics.link({
              ...Analytics.getUtagData(["page_name"]),
              page_form_event: "error",
              page_errors: `${
                res?.error?.data?.result?.message
                  ? res.error.data.result.message
                  : res?.error?.data?.message
                  ? res.error.data.message
                  : ""
              }`,
              page_errors_code: `chk-dxl-g-id-${res?.error?.status}`,
            });
          }

          redirectToPayment();
        })
        .catch((err) => {
          if (err) {
            Analytics.link({
              ...Analytics.getUtagData(["page_name"]),
              page_form_event: "error",
              page_errors: `${err.message ? err.message : ""}`,
              page_errors_code: `chk-dxl-g-id-${err?.response?.status}`,
            });
          }
          redirectToPayment();
        });
    };

    const redirectToPayment = () => {

      if (isMiniApp) {

        window.location = `${EnvConfig.miniAppUrl}?orderId=${orderId}`;
        return;
      }

      window.setTimeout(() => {
        document.ucfCheckout.submit.bind(document.ucfCheckout);
        document.ucfCheckout.submit();
      }, 800);
    };

    const updateRetryCount = () => {
      const newCount = parseInt(retryCount) + 1;
      setRetryCount(newCount);
      setCookieConsentWrapper(
        `cookie_${config.pay_fail_count}`,
        [config.cookie_category.strictly_necessary],
        `document.cookie='${config.pay_fail_count}=${newCount};path=/'`
      );
    };

    return (
      <>
        <CustomContainer className={styles["error-container"]}>
          <CustomContainer className={styles["icon"]} id="error-img">
            <Image
              img_path={`${imagePrefix}/assets/images/error-warning-red.svg`}
            />
          </CustomContainer>
          <CustomContainer className={styles["heading"]} id="error-title">
            {headerText}
          </CustomContainer>
          <CustomContainer className={styles["content"]} id="error-desc">
            {descriptionText}
          </CustomContainer>
          {noteText !== "" ? (
            <CustomContainer className={styles["content"]} id="error-note">
              {noteText}
            </CustomContainer>
          ) : null}
          <CustomContainer className={styles["button-wrapper"]}>
            {retryCount < 2 && !technicalError && (
              <CTAButton
                className={styles["try-btn"]}
                handleClick={handleRetry}
                variant={retryBtnConfig.buttonStyle}
                id={`${retryBtnConfig.buttonLabel}-btn`}
              >
                {retryBtnConfig.buttonLabel}
              </CTAButton>
            )}
            {retryCount === 2 && !technicalError && (
              <CTAButton
                className={styles["exit-btn"]}
                handleClick={handleExitCheckout}
                variant={exitBtnConfig.buttonStyle}
                id={`${exitBtnConfig.buttonLabel}-btn`}
              >
                {userLoggedIn
                  ? labels.lbl_go_to_order_page_btn
                  : exitBtnConfig.buttonLabel}
              </CTAButton>
            )}
            {technicalError && (
              <CTAButton
                className={styles["exit-btn"]}
                handleClick={handleExitCheckout}
                variant={exitBtnConfig.buttonStyle}
              >
                {exitBtnConfig.buttonLabel}
              </CTAButton>
            )}
          </CustomContainer>
          <form
            method="post"
            name="ucfCheckout"
            id="ucfCheckout"
            action={
              paymentOptionsEnabled && paymentUrl
                ? paymentUrl
                : paymentConfig.paymentGatewayUrl
            }
          >
            <input
              type="hidden"
              name="PayserverUser"
              id="PayserverUser"
              value={paymentConfig.payServerUser}
            />
            <input
              type="hidden"
              name="transactionStatusCallbackUrl"
              id="transactionStatusCallbackUrl"
              value={paymentConfig.transactionStatusCallbackUrl}
            />
            <input
              type="hidden"
              name="transactionCompletedUrl"
              id="transactionCompletedUrl"
              value={paymentCallbackUrl}
            />
            <input
              type="hidden"
              name="redirectURL"
              id="redirectURL"
              value={paymentCallbackUrl}
            />
            <input
              type="hidden"
              name="failureURL"
              id="failureURL"
              value={paymentCallbackUrl}
            />
            <input
              type="hidden"
              name="TransactionStatusCallbackFormat"
              id="TransactionStatusCallbackFormat"
              value={paymentConfig.transactionStatusCallbackFormat}
            />
            <input
              type="hidden"
              name="TransactionStatusCallbackMethod"
              id="TransactionStatusCallbackMethod"
              value={paymentConfig.transactionStatusCallbackMethod}
            />
            <input
              type="hidden"
              name="MerchantReference"
              id="MerchantReference"
              value={postOrderData?.productOrderId}
            />
            <input
              type="hidden"
              name="Currency"
              id="Currency"
              value={paymentConfig.currency}
            />
            <input
              type="hidden"
              name="TransactionType"
              id="TransactionType"
              value={paymentConfig.transactionType}
            />
            <input
              type="hidden"
              name="MerchantIdentifier"
              id="MerchantIdentifier"
              value={paymentConfig.merchantIdentifier}
            />
            <input
              type="hidden"
              name="CustomerIdentifier"
              id="CustomerIdentifier"
              value={customerIdentifier}
            />
            <input
              type="hidden"
              name="EncXML"
              id="EncXML"
              value={postOrderData?.payment?.encryptedPaymentDetails}
            />
            <input
              type="hidden"
              name="EncType"
              id="EncType"
              value={paymentConfig.encType}
            />
          </form>
        </CustomContainer>
      </>
    );
  }
);

CheckoutPaymentError.propTypes = {
  navigate: PropTypes.func,
  paymentStatus: PropTypes.object,
  customerStatus: PropTypes.string,
  isMiniApp: PropTypes.bool,
};

const mapDispatchToProps = {
  setNormalCheckoutCurrentStep,
  setPostCheckoutCurrentStep,
  removeCreditCheckStates,
  setPaymentMethodUrl,
  setPostOrderResponse,
};

export default connect(null, mapDispatchToProps)(CheckoutPaymentError);
